/**
 * Dos pestañas, una sesión — `OD-0063` D-05 UNA PESTAÑA O DOS.
 *
 * La empresa activa es de la SESIÓN, no de la pestaña: si en una se cambia de
 * empresa, la otra sigue pintando la anterior pero cada petición suya ya va a
 * la nueva. Leer así engaña, y escribir así escribe en la empresa
 * equivocada. `ASESORIA` no escribe, pero el titular que también lleva otras
 * empresas como gestor sí.
 *
 * ```text
 * ANTES DE CAMBIAR  se pregunta si hay otra pestaña abierta (`hayOtras`), y si
 *                   la hay se avisa y se pide confirmar
 * AL CAMBIAR        se avisa a las demás (`avisarCambio`), y cada una que
 *                   estuviera en otra empresa se tapa entera hasta recargar
 * ```
 *
 * `BroadcastChannel` solo habla entre pestañas del MISMO origen y del mismo
 * navegador, que es justo lo que comparte la cookie de sesión. Sin él (un
 * navegador viejo) no se avisa de nada: no hay otra forma de saberlo, y la
 * API sigue autorizando cada petición con la empresa que tiene la sesión.
 */

const CANAL = "faktelio.sesion";

type Mensaje =
  | { readonly tipo: "quien-hay" }
  | { readonly tipo: "aqui" }
  | { readonly tipo: "empresa-cambiada"; readonly organizationId: string };

/** Lo justo de `BroadcastChannel`: así se prueba con el de Node. */
export type Canal = {
  postMessage(m: unknown): void;
  addEventListener(tipo: "message", f: (e: MessageEvent) => void): void;
  removeEventListener(tipo: "message", f: (e: MessageEvent) => void): void;
  close(): void;
};

export type Pestanas = {
  /** Si alguna otra pestaña contesta en `esperaMs`. */
  hayOtras(esperaMs?: number): Promise<boolean>;
  /** La sesión acaba de pasar a `organizationId`. */
  avisarCambio(organizationId: string): void;
  /** `f` recibe la empresa a la que ha pasado la sesión desde OTRA pestaña. */
  alCambiar(f: (organizationId: string) => void): () => void;
  cerrar(): void;
};

function esMensaje(v: unknown): v is Mensaje {
  if (typeof v !== "object" || v === null) return false;
  const t = (v as { tipo?: unknown }).tipo;
  if (t === "quien-hay" || t === "aqui") return true;
  return t === "empresa-cambiada" && typeof (v as { organizationId?: unknown }).organizationId === "string";
}

/** `abrir` da el canal, o `null` si el navegador no tiene `BroadcastChannel`. */
export function pestanas(abrir: () => Canal | null): Pestanas {
  const canal = abrir();
  if (canal === null) {
    return { hayOtras: async () => false, avisarCambio: () => undefined, alCambiar: () => () => undefined, cerrar: () => undefined };
  }
  // Toda pestaña contesta a quien pregunta, mire o no el panel.
  const contestar = (e: MessageEvent): void => {
    if (esMensaje(e.data) && e.data.tipo === "quien-hay") canal.postMessage({ tipo: "aqui" } satisfies Mensaje);
  };
  canal.addEventListener("message", contestar);
  return {
    hayOtras: (esperaMs = 200) =>
      new Promise<boolean>((resolver) => {
        const oir = (e: MessageEvent): void => {
          if (!esMensaje(e.data) || e.data.tipo !== "aqui") return;
          clearTimeout(reloj);
          canal.removeEventListener("message", oir);
          resolver(true);
        };
        const reloj = setTimeout(() => {
          canal.removeEventListener("message", oir);
          resolver(false);
        }, esperaMs);
        canal.addEventListener("message", oir);
        canal.postMessage({ tipo: "quien-hay" } satisfies Mensaje);
      }),
    avisarCambio: (organizationId) => canal.postMessage({ tipo: "empresa-cambiada", organizationId } satisfies Mensaje),
    alCambiar: (f) => {
      const oir = (e: MessageEvent): void => {
        if (esMensaje(e.data) && e.data.tipo === "empresa-cambiada") f(e.data.organizationId);
      };
      canal.addEventListener("message", oir);
      return () => canal.removeEventListener("message", oir);
    },
    cerrar: () => {
      canal.removeEventListener("message", contestar);
      canal.close();
    },
  };
}

/** El canal del navegador, uno por pestaña. */
let deEstaPestana: Pestanas | null = null;
export function pestanasDelNavegador(): Pestanas {
  deEstaPestana ??= pestanas(() => (typeof BroadcastChannel === "function" ? new BroadcastChannel(CANAL) : null));
  return deEstaPestana;
}
